/**
 * CursorPaginator — load-more primitive for cursor-paginated list pages.
 *
 * Pattern extracted from audit-log.tsx. Pair with TanStack Query accumulation:
 *   - keep an `accumulated: Row[]` state
 *   - keep a `cursor: string | null` state
 *   - useQuery includes cursor in its key
 *   - on data: if cursor==null replace; else append
 *   - reset cursor + accumulated when filters change
 *
 * Visual contract:
 *   - hasMore=true  → "Showing N items" left + "Load N more" button right
 *   - hasMore=false → entirely hidden (caller usually shows "End of list")
 *   - loading=true  → button label flips to "Loading…" and shows spinner
 */
import { Loader2 } from 'lucide-react';
import { Button } from '@/components/ui/button';

export interface CursorPaginatorProps {
  /** True when the API returned a non-null next_cursor for the latest page. */
  hasMore: boolean;
  /** True while the next-page request is in flight (use query.isFetching). */
  loading: boolean;
  /** Fires when the user clicks "Load N more". */
  onLoadMore: () => void;
  /** Optional — total accumulated row count. Renders "Showing N items" on the left. */
  itemsShown?: number;
  /** Page size used in the load-more label ("Load 50 more"). Defaults to 50. */
  pageSize?: number;
  /** Optional className passed through to the wrapper. */
  className?: string;
}

export function CursorPaginator({
  hasMore,
  loading,
  onLoadMore,
  itemsShown,
  pageSize = 50,
  className
}: CursorPaginatorProps): JSX.Element | null {
  // Hide entirely when there are no more pages (and we have at least one item).
  // The "end of list" hint, if any, is owned by the caller — keeping the primitive
  // strictly responsible for the load-more affordance.
  if (!hasMore) {
    if (typeof itemsShown === 'number' && itemsShown > 0) return null;
    if (typeof itemsShown === 'undefined') return null;
  }

  return (
    <div
      className={
        'mt-vt-4 flex flex-wrap items-center justify-between gap-vt-2 ' +
        (className ?? '')
      }
    >
      <span className="text-vt-12 text-vt-text-muted">
        {typeof itemsShown === 'number' ? `Showing ${itemsShown} item${itemsShown === 1 ? '' : 's'}` : ''}
      </span>
      {hasMore ? (
        <Button
          variant="secondary"
          size="sm"
          onClick={onLoadMore}
          disabled={loading}
          aria-busy={loading || undefined}
        >
          {loading ? (
            <>
              <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" />
              <span>Loading…</span>
            </>
          ) : (
            <span>Load {pageSize} more</span>
          )}
        </Button>
      ) : null}
    </div>
  );
}
